import { FC, useContext } from 'react'

import { message, Tooltip, Upload, UploadProps } from 'antd'
import { RcFile } from 'antd/es/upload'
import dayjs from 'dayjs'

import { ApiResult, isSuccess } from '@/httpRequest'
import { TUploadResp } from '@/httpRequest/common/upload'
import { EmojiData } from '@/pages/MesperyIM/EmojiData'
import { useMessageAtom } from '@/pages/MesperyIM/store/message'
import { useUserAtom } from '@/store/user'
import { getGlobalConfig } from '@/utils'
import { handleClickSendMessage, handleSendSuccess } from '@/utils/trackSensors'

import { CUSTOMER_SERVICE_ACCOUNT_MAP } from './ChatPanelInput'
import { ChatPanelInputContext } from '../context'
import useSWRSessionList from '../hooks/useSWRSessionList'
import styles from '../message.module.less'
import { useWs } from '../providers/WebsocketProvider'
import { EMessageType } from '../types/message'
import { sortSessionsById } from '../utils'

const baseURL = getGlobalConfig().VITE_IM_API_URL

export interface IToolBarProps {
  disabled?: boolean
}

export const ToolBar: FC<IToolBarProps> = (props) => {
  const { disabled } = props
  const { setChatRecords } = useMessageAtom()
  const {
    currentConversation,
    messageData,
    setMessageData,
    conversationList,
    setConversationList
  } = useMessageAtom()
  const { textareaRef, inputChange } = useContext(ChatPanelInputContext)
  const { sendRequest } = useWs()
  const { user } = useUserAtom()
  const { mutate, data: swrSessionList } = useSWRSessionList()

  const beforeUpload = (file: RcFile) => {
    if (file.size > 20 * 1024 * 1024) {
      message.error('Please upload an image smaller than 20M')
      return Upload.LIST_IGNORE
    }

    return true
  }

  const upload = async (file: File) => {
    const token = localStorage.getItem('token')
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await fetch(`${baseURL}/imApi/common/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })

      const data: ApiResult<TUploadResp> = await response.json()
      if (isSuccess(data)) {
        return { url: data.data.url }
      } else {
        throw new Error('Fail to upload')
      }
    } catch (err) {
      throw new Error('Fail to upload')
    }
  }

  const handleSendImgMessage = async (file: File) => {
    const uploadResult = await upload(file)
    if (!uploadResult.url) {
      return { url: '' }
    }
    const sendData = {
      receiver_type: currentConversation?.session_type,
      receiver_id: currentConversation?.session_id,
      message_type: EMessageType.ImgMsg,
      message_content: uploadResult.url
    }

    const sendTime = dayjs().unix()
    handleClickSendMessage(
      currentConversation?.session_id || '',
      currentConversation?.session_type || 0,
      EMessageType.ImgMsg,
      CUSTOMER_SERVICE_ACCOUNT_MAP.get(user.id) || user.id
    )
    const resp = await sendRequest<typeof sendData>('SEND_MESSAGE', sendData)

    if (messageData?.list && resp) {
      // 全新的会话，需要重新获取会话列表数据
      if (
        !conversationList?.some(
          (item) => item.session_id === currentConversation?.session_id
        )
      ) {
        mutate(swrSessionList, {
          revalidate: (pageData, key) =>
            key === '/imApi/session/list?page=1&size=20'
        })
      }
      handleSendSuccess(
        currentConversation?.session_id || '',
        currentConversation?.session_type || 0,
        EMessageType.ImgMsg,
        CUSTOMER_SERVICE_ACCOUNT_MAP.get(user.id) || user.id
      )
      messageData.list = [
        ...messageData.list,
        {
          id: '',
          created_at: String(sendTime),

          message_content: uploadResult.url,

          message_type: EMessageType.ImgMsg,

          sender_user_id: CUSTOMER_SERVICE_ACCOUNT_MAP.get(user.id) || user.id
        }
      ]
      setMessageData({ ...messageData })

      const sessionList = conversationList?.map((item) => {
        if (item.session_id === currentConversation?.session_id) {
          item.last_message_content = 'image'
          item.last_message_created_at = sendTime
          item.last_read_at = sendTime
          item.last_message_type = EMessageType.ImgMsg
          item.last_message_sender_id =
            CUSTOMER_SERVICE_ACCOUNT_MAP.get(user.id) || user.id
        }
        return item
      })
      let sortList = sessionList
      if (sessionList) {
        sortList = sortSessionsById(
          sessionList,
          currentConversation?.session_id
        )
      }
      setConversationList(sortList)

      setTimeout(() => {
        // 获取容器元素
        const container = document.getElementById('scrollableDivIM')
        // 将容器滚动到底部
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      }, 100)
    }
  }

  const uploadOption: UploadProps = {
    name: 'file',
    accept: 'image/*',
    customRequest: (options) => {
      handleSendImgMessage(options.file as unknown as File)
    },
    beforeUpload
  }

  const handleSendEmoji = async (emoji: string) => {
    const textArea = textareaRef?.current?.resizableTextArea?.textArea
    if (textArea) {
      const startPos = textArea.selectionStart
      const endPos = textArea.selectionEnd
      const value = textArea.value

      const newValue = value.slice(0, startPos) + emoji + value.slice(endPos)
      inputChange(newValue)
      const newCursorPos = startPos + emoji.length
      textArea.selectionStart = newCursorPos
      textArea.selectionEnd = newCursorPos
      textArea.focus()
    }
  }
  return (
    <div className="ToolBar">
      {disabled ? (
        <a className="btn-action-face" />
      ) : (
        <Tooltip
          color={'#2F3132'}
          title={
            <div className={`${styles.emojiBox} emojiBox`}>
              <ul>
                {EmojiData.map((emoji, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      handleSendEmoji(emoji)
                    }}
                  >
                    {emoji}
                  </li>
                ))}
              </ul>
            </div>
          }
          placement="top"
          overlayClassName="emojiToolTip"
        >
          <a className="btn-action-face" />
        </Tooltip>
      )}

      {disabled ? (
        <a className="btn-action-pic" />
      ) : (
        <Upload {...uploadOption} showUploadList={false}>
          <a className="btn-action-pic" />
        </Upload>
      )}

      {/* <a className="btn-action-box" onClick={() => setChatRecords(true)} /> */}
    </div>
  )
}
